'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { DEFAULT_SLIPPAGE_TOLERANCE, type MarketId, MathLib } from '@morpho-org/blue-sdk';
import { parseApiAsset, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import MarketRecapV2 from '@/components/Bundler/ConfirmDialog/Recaps/MarketRecapV2';
import { useProtectedLltv } from '@/components/common/AutoDeleverage/ProtectedLltv';
import InfoCallout from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/InfoCallout';
import { MARKET_GEO_RESTRICTION_MESSAGE } from '@/config/marketGeoRestriction';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getMarketGeoRestrictionActionProps } from '@/helpers/marketGeoRestriction';
import { getMarketActionError } from '@/hooks/operation/market/v2/marketActionError';
import { useMarketBlue } from '@/hooks/operation/market/v2/useMarketBlue';
import { useMarketReallocations } from '@/hooks/operation/market/v2/useMarketReallocations';
import { useMarketRefinanceBuilder } from '@/hooks/operation/market/v2/useMarketRefinanceBuilder';
import { useMarketGeoRestriction } from '@/hooks/useMarketGeoRestriction';
import { orpc } from '@/lib/orpc/client';
import { converter } from '@/selectors/converter';
import { ActionButtons } from '../common/ActionButtons';
import { OperationSummaryDialog } from '../common/Dialog';
import { SelectMarket } from '../common/SelectMarket';
import RefinanceReviewDialog from './RefinanceReviewDialog';
import { getRefinanceFinalPositionRisk } from './refinanceRisk';
import { toRefinanceDialogMarket } from './toRefinanceDialogMarket';
import { useRefinancePositionPreview } from './useRefinancePositionPreview';
export interface RefinanceDialogProps {
  marketId: MarketId;
  collateralAddress: Address;
  loanAddress: Address;
}

/**
 * Refinance input step: moves a borrow position (collateral + debt) from a SOURCE market to a TARGET
 * market. Both markets' `MarketParams` come from the API via `converter.getMarketParams`; the
 * position preview is pure JS-math (no evm-simulation — that runs in {@link RefinanceReviewDialog}).
 * The finalize button opens the review, then `ConfirmDialogV2`, like every other flow. Gated by the
 * `positionManagerRefinance` LD flag at the entry point.
 */
const RefinanceDialog = t0 => {
  const $ = _c(85);
  const {
    marketId,
    collateralAddress,
    loanAddress
  } = t0;
  const {
    targetChainId,
    account
  } = useWeb3Context();
  const {
    toggleDialog
  } = useDialogContext();
  let t1;
  if ($[0] !== account.isConnected || $[1] !== collateralAddress || $[2] !== loanAddress || $[3] !== targetChainId) {
    t1 = orpc.market.refinanceMarkets.queryOptions({
      input: account.isConnected ? {
        chainId: targetChainId,
        loanAssetAddress: loanAddress,
        collateralAssetAddress: collateralAddress
      } : skipToken
    });
    $[0] = account.isConnected;
    $[1] = collateralAddress;
    $[2] = loanAddress;
    $[3] = targetChainId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    data,
    isLoading: isLoadingMarkets,
    isError: isMarketsReadError
  } = useQuery(t1);
  const markets = data?.items.map(toRefinanceDialogMarket);
  const [selectedMarketId, setSelectedMarketId] = useState();
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const apiMarketFrom = markets?.find(market => market.marketId === marketId);
  const availableMarkets = markets?.filter(market_0 => market_0.marketId !== marketId);
  const apiMarketTo = availableMarkets?.find(market_1 => market_1.marketId === selectedMarketId) ?? availableMarkets?.[0];
  const fromGeoRestriction = useMarketGeoRestriction({
    chainId: targetChainId,
    assetAddresses: [apiMarketFrom?.collateralAsset?.address, apiMarketFrom?.loanAsset.address]
  });
  const toGeoRestriction = useMarketGeoRestriction({
    chainId: targetChainId,
    assetAddresses: [apiMarketTo?.collateralAsset?.address, apiMarketTo?.loanAsset?.address]
  });
  const geoRestriction = {
    isRestricted: fromGeoRestriction.isRestricted || toGeoRestriction.isRestricted,
    isPending: fromGeoRestriction.isPending || toGeoRestriction.isPending,
    message: MARKET_GEO_RESTRICTION_MESSAGE
  };
  const collateralToken = parseApiAsset(apiMarketFrom?.collateralAsset);
  const loanToken = parseApiAsset(apiMarketFrom?.loanAsset);
  const sourceMarketParams = apiMarketFrom ? converter.getMarketParams(apiMarketFrom) : undefined;
  const targetMarketParams = apiMarketTo ? converter.getMarketParams(apiMarketTo) : undefined;
  const {
    positionData: sourcePositionData
  } = useMarketBlue(sourceMarketParams);
  const sourceCollateral = sourcePositionData?.collateral ?? ZERO;
  const sourceDebt = sourcePositionData?.borrowAssets ?? ZERO;
  const hasSourcePosition = !!sourcePositionData && (sourcePositionData.borrowShares > ZERO || sourcePositionData.collateral > ZERO);
  const {
    reallocations: targetReallocations,
    reallocationError,
    isLoading: isReallocationLoading
  } = useMarketReallocations({
    marketParams: targetMarketParams,
    operation: "borrow",
    amount: sourceDebt,
    enabled: isDefined(targetMarketParams)
  });
  const {
    targetPositionData,
    buildError,
    marketError,
    isPositionLoading
  } = useMarketRefinanceBuilder({
    sourceMarketParams,
    targetMarketParams,
    targetReallocations
  });
  let t2;
  if ($[5] !== setIsSelectOpen) {
    t2 = nextMarketId => {
      setSelectedMarketId(nextMarketId);
      setIsSelectOpen(false);
    };
    $[5] = setIsSelectOpen;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleSelectMarket = t2;
  let t3;
  if ($[7] !== toggleDialog) {
    t3 = () => {
      toggleDialog(false);
    };
    $[7] = toggleDialog;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onCloseDialog = t3;
  let t4;
  if ($[9] !== sourceCollateral || $[10] !== sourceDebt || $[11] !== sourcePositionData || $[12] !== targetPositionData) {
    t4 = {
      sourcePositionData,
      targetPositionData,
      collateralAmount: sourceCollateral,
      movedDebt: sourceDebt
    };
    $[9] = sourceCollateral;
    $[10] = sourceDebt;
    $[11] = sourcePositionData;
    $[12] = targetPositionData;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const {
    sourceCurrent,
    targetCurrent,
    sourceInitial,
    targetInitial,
    sourceAfterJs,
    targetAfterJs
  } = useRefinancePositionPreview(t4);
  const isErrorNoMarkets = !isLoadingMarkets && (!isDefined(apiMarketFrom) || !isDefined(apiMarketTo));
  const marketsError = isMarketsReadError && !data ? "The markets could not be loaded. Try again later." : isErrorNoMarkets ? data?.partial ? "Some market data could not be loaded, so this list may be incomplete." : "No available markets to refinance your position into." : null;
  const t5 = sourceMarketParams?.lltv;
  const t6 = sourceMarketParams?.id;
  let t7;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== targetChainId) {
    t7 = {
      lltv: t5,
      marketId: t6,
      chainId: targetChainId
    };
    $[14] = t5;
    $[15] = t6;
    $[16] = targetChainId;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const sourceProtectedLltv = useProtectedLltv(t7);
  const t8 = targetMarketParams?.lltv;
  const t9 = targetMarketParams?.id;
  let t10;
  if ($[18] !== t8 || $[19] !== t9 || $[20] !== targetChainId) {
    t10 = {
      lltv: t8,
      marketId: t9,
      chainId: targetChainId
    };
    $[18] = t8;
    $[19] = t9;
    $[20] = targetChainId;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const targetProtectedLltv = useProtectedLltv(t10);
  const finalPositionRisk = getRefinanceFinalPositionRisk({
    sourceLtv: sourceAfterJs?.ltv,
    targetLtv: targetAfterJs?.ltv,
    sourceProtectedLltv,
    targetProtectedLltv
  });
  const targetLiquidity = targetPositionData?.market?.liquidity;
  let t11;
  if ($[22] !== targetReallocations) {
    t11 = targetReallocations ?? [];
    $[22] = targetReallocations;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const plannedReallocationAssets = t11.reduce(_temp, ZERO);
  const requiredTargetLiquidity = MathLib.wMulUp(sourceDebt, MathLib.WAD + DEFAULT_SLIPPAGE_TOLERANCE);
  const isTargetLiquidityInsufficient = isDefined(targetLiquidity) && sourceDebt > ZERO && requiredTargetLiquidity > targetLiquidity + plannedReallocationAssets;
  const isFinalizeDisabled = !sourceMarketParams || !targetMarketParams || account.isWrongChain || !!buildError || !hasSourcePosition || !!marketError || isReallocationLoading || isTargetLiquidityInsufficient || finalPositionRisk.isAtOrAboveProtectedLltv;
  let t12;
  if ($[24] !== apiMarketFrom || $[25] !== collateralToken || $[26] !== loanToken || $[27] !== sourceAfterJs || $[28] !== sourceCollateral || $[29] !== sourceCurrent || $[30] !== sourceDebt || $[31] !== sourceInitial || $[32] !== sourceMarketParams || $[33] !== targetChainId) {
    t12 = apiMarketFrom && sourceMarketParams && collateralToken && loanToken ? <MarketRecapV2 marketId={apiMarketFrom.marketId} chainId={targetChainId} collateralAsset={collateralToken} loanAsset={loanToken} mode="repayWithdraw" collateralAmount={sourceCollateral} loanAmount={sourceDebt} initial={sourceInitial} final={sourceAfterJs} lltv={sourceMarketParams.lltv} marketForRewards={apiMarketFrom} avgBorrowApy={apiMarketFrom.state?.avgBorrowApy} isInitialPositionLoading={!sourceCurrent} isFinalPositionLoading={!sourceCurrent} isSimulationFallback={false} /> : null;
    $[24] = apiMarketFrom;
    $[25] = collateralToken;
    $[26] = loanToken;
    $[27] = sourceAfterJs;
    $[28] = sourceCollateral;
    $[29] = sourceCurrent;
    $[30] = sourceDebt;
    $[31] = sourceInitial;
    $[32] = sourceMarketParams;
    $[33] = targetChainId;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const fromRecap = t12;
  let t13;
  if ($[35] !== apiMarketTo || $[36] !== collateralToken || $[37] !== loanToken || $[38] !== sourceCollateral || $[39] !== sourceDebt || $[40] !== targetAfterJs || $[41] !== targetChainId || $[42] !== targetCurrent || $[43] !== targetInitial || $[44] !== targetMarketParams) {
    t13 = apiMarketTo && targetMarketParams && collateralToken && loanToken ? <MarketRecapV2 marketId={apiMarketTo.marketId} chainId={targetChainId} collateralAsset={collateralToken} loanAsset={loanToken} mode="supplyBorrow" collateralAmount={sourceCollateral} loanAmount={sourceDebt} initial={targetInitial} final={targetAfterJs} lltv={targetMarketParams.lltv} marketForRewards={apiMarketTo} avgBorrowApy={apiMarketTo.state?.avgBorrowApy} isInitialPositionLoading={!targetCurrent} isFinalPositionLoading={!targetCurrent} isSimulationFallback={false} /> : null;
    $[35] = apiMarketTo;
    $[36] = collateralToken;
    $[37] = loanToken;
    $[38] = sourceCollateral;
    $[39] = sourceDebt;
    $[40] = targetAfterJs;
    $[41] = targetChainId;
    $[42] = targetCurrent;
    $[43] = targetInitial;
    $[44] = targetMarketParams;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  const toRecap = t13;
  const tooltipContent = getMarketActionError({
    buildError: buildError ?? (isTargetLiquidityInsufficient ? reallocationError : undefined) ?? marketError
  }) ?? (isTargetLiquidityInsufficient ? "Not enough liquidity available" : undefined);
  const isButtonLoading = account.isConnected && (isPositionLoading || isReallocationLoading);
  const t14 = getMarketGeoRestrictionActionProps({
    geoRestriction,
    isFinalizeDisabled,
    isLoading: isButtonLoading,
    finalizeLabel: "Review refinance",
    tooltipContent
  });
  let geoActionProps;
  let shouldBlockFinalize;
  if ($[46] !== t14) {
    ({
      shouldBlockFinalize,
      ...geoActionProps
    } = t14);
    $[46] = t14;
    $[47] = geoActionProps;
    $[48] = shouldBlockFinalize;
  } else {
    geoActionProps = $[47];
    shouldBlockFinalize = $[48];
  }
  let t15;
  if ($[49] !== apiMarketFrom || $[50] !== apiMarketTo || $[51] !== collateralToken || $[52] !== loanToken || $[53] !== shouldBlockFinalize || $[54] !== sourceMarketParams || $[55] !== targetChainId || $[56] !== targetMarketParams || $[57] !== targetReallocations || $[58] !== toggleDialog) {
    t15 = () => {
      if (!sourceMarketParams || !targetMarketParams || !apiMarketFrom || !apiMarketTo) {
        return;
      }
      if (!collateralToken || !loanToken) {
        return;
      }
      if (shouldBlockFinalize) {
        return;
      }
      toggleDialog(<RefinanceReviewDialog chainId={targetChainId} sourceMarketParams={sourceMarketParams} targetMarketParams={targetMarketParams} apiMarketFrom={apiMarketFrom} apiMarketTo={apiMarketTo} collateralToken={collateralToken} loanToken={loanToken} targetReallocations={targetReallocations} />);
    };
    $[49] = apiMarketFrom;
    $[50] = apiMarketTo;
    $[51] = collateralToken;
    $[52] = loanToken;
    $[53] = shouldBlockFinalize;
    $[54] = sourceMarketParams;
    $[55] = targetChainId;
    $[56] = targetMarketParams;
    $[57] = targetReallocations;
    $[58] = toggleDialog;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  const handleFinalize = t15;
  let t16;
  if ($[60] !== data?.partial) {
    t16 = data?.partial && <InfoCallout variant="warning" data-testid="refinance-markets-partial-notice">Some market data could not be loaded, so this list may be incomplete.</InfoCallout>;
    $[60] = data?.partial;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== availableMarkets) {
    t17 = availableMarkets ?? [];
    $[62] = availableMarkets;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== apiMarketTo || $[65] !== handleSelectMarket || $[66] !== isLoadingMarkets || $[67] !== isSelectOpen || $[68] !== setIsSelectOpen || $[69] !== t17) {
    t18 = <SelectMarket isOpen={isSelectOpen} direction="row" markets={t17} selectedMarket={apiMarketTo} isLoading={isLoadingMarkets} onOpenChange={setIsSelectOpen} onSelect={handleSelectMarket} buttonLabel="Switch to" />;
    $[64] = apiMarketTo;
    $[65] = handleSelectMarket;
    $[66] = isLoadingMarkets;
    $[67] = isSelectOpen;
    $[68] = setIsSelectOpen;
    $[69] = t17;
    $[70] = t18;
  } else {
    t18 = $[70];
  }
  let t19;
  if ($[71] !== geoActionProps || $[72] !== handleFinalize || $[73] !== isErrorNoMarkets) {
    t19 = !isErrorNoMarkets && <ActionButtons handleFinalize={handleFinalize} {...geoActionProps} />;
    $[71] = geoActionProps;
    $[72] = handleFinalize;
    $[73] = isErrorNoMarkets;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  let t20;
  if ($[75] !== fromRecap || $[76] !== isLoadingMarkets || $[77] !== marketId || $[78] !== marketsError || $[79] !== onCloseDialog || $[80] !== t16 || $[81] !== t18 || $[82] !== t19 || $[83] !== toRecap) {
    t20 = <OperationSummaryDialog title="Refinance Loan" key={marketId} loading={isLoadingMarkets} error={marketsError} onClose={onCloseDialog} topSlot={t16} headerActionSlot={t18} initialPositionSlot={fromRecap} finalPositionSlot={toRecap} actionSlot={t19} />;
    $[75] = fromRecap;
    $[76] = isLoadingMarkets;
    $[77] = marketId;
    $[78] = marketsError;
    $[79] = onCloseDialog;
    $[80] = t16;
    $[81] = t18;
    $[82] = t19;
    $[83] = toRecap;
    $[84] = t20;
  } else {
    t20 = $[84];
  }
  return t20;
};
export default RefinanceDialog;
function _temp(sum, reallocation) {
  return sum + reallocation.assets;
}